import clsx from 'clsx';
import React from 'react';

import { DropLine } from '@gitbook/react-uikit';
import type { Block } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';

import { useDroppableWithCallback } from '../../DragAndDrop';

/**
 * Render a tab with insertion line around it.
 */
export function BlockTabsHeaderItemControls(props: {
    active: boolean;
    editor: EditorContainer;
    tab: Block;
    isLast: boolean;
    onDropBefore: (tabId: string) => void;
    onDropAfter: (tabId: string) => void;
    onInsertLeft: () => void;
    onInsertRight: () => void;
    onDelete: () => void;
    children: React.ReactNode;
}): React.ReactElement {
    const {
        active,
        editor,
        tab,
        isLast,
        onDropBefore,
        onDropAfter,
        onInsertLeft,
        onInsertRight,
        onDelete,
        children,
        ...rest
    } = props;

    return (
        <div className={clsx('relative shrink-1', active && 'shrink-0')} {...rest}>
            {/* Insert Before */}
            <TabInsertionLine position="before" onDrop={onDropBefore} onInsert={onInsertLeft} />

            {/* Content */}
            {children}

            {/* Insert After */}
            {isLast ? (
                <TabInsertionLine position="after" onDrop={onDropAfter} onInsert={onInsertRight} />
            ) : null}
        </div>
    );
}

/**
 * An hoverable zone to insert a new tab.
 */
function TabInsertionLine(props: {
    position: 'before' | 'after';
    onDrop: (tabId: string) => void;
    onInsert: () => void;
}): React.ReactElement {
    const { position, onDrop, onInsert } = props;

    //
    // Handle drop of other tabs
    //
    const drop = useDroppableWithCallback({
        shouldAccept: React.useCallback((item) => {
            if (item.type === 'tab') {
                return true;
            }

            return false;
        }, []),
        onDrop: (item) => {
            if (item.type === 'tab') {
                onDrop(item.id);
            }
        },
    });

    return (
        <DropLine
            ref={drop.setNodeRef}
            dropHovered={drop.isOver}
            position={position === 'before' ? 'left' : 'right'}
            insertButton={{
                onPress: onInsert,
                label: 'Insert tab',
            }}
        />
    );
}
